import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Drawer, Divider, IconButton } from '@mui/material';
import { useHttp } from 'hooks/use-fetch';
import { useQuery } from 'react-query';
import { fetchBulkUploadList, fetchBulkUploadDownload } from 'pages/Dashboard/services/bulkUpload.service';
import Theme from 'theme';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import Toast, { ToastProps, ToastType } from 'components/Common/Toast';
import Spinner from 'components/Common/Spinner';
import { BulkUploadListItem } from 'pages/Dashboard/types/bulkUpload.types';
import CloseIcon from '@mui/icons-material/Close';

interface BulkUploadHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function BulkUploadHistoryDrawer({ open, onClose }: BulkUploadHistoryDrawerProps) {
  const [forceOpen, setForceOpen] = useState(false);
  const [uploadToastProps, setUploadToastProps] = React.useState<ToastProps | null>(null);

  const { http, providerId } = useHttp();

  useEffect(() => {
    if (sessionStorage.getItem('openBulkUploadHistory') === 'true') {
      setForceOpen(true);
      sessionStorage.removeItem('openBulkUploadHistory');
    }
  }, []);

  const isDrawerOpen = open || forceOpen;

  function handleClose() {
    setForceOpen(false);
    sessionStorage.removeItem('openBulkUploadHistory');
    if (onClose) onClose();
  }

  function downloadFromPresignedUrl(url: string, filename: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const { data, isLoading } = useQuery([
    'patient-bulk-upload-list',
    providerId,
  ], fetchBulkUploadList(http.get));

  const downloadBulkUpload = fetchBulkUploadDownload(http.post);

  const handleFileDownload = async (filename: string, fileKey?: string) => {
    try {
      trackMixpanelEvent(MixpanelEventName.BULK_UPLOAD_PATIENTS_FILE_DOWNLOAD_CLICKED, {
        filename,
      });
      // fileKey is required for the new API
      const result = await downloadBulkUpload({ queryKey: ['', providerId, fileKey], meta: undefined });
      // Assume result contains { presignedUrl: 'presigned-url' }
      if (result?.presignedUrl) {
        downloadFromPresignedUrl(result.presignedUrl, filename);
      }
      else if (typeof result === 'string') {
        // fallback: open in new tab
        window.open(result, '_blank');
      }
    }
    catch (e) {
      setUploadToastProps({
        type: ToastType.error,
        message: 'Unable to download file. Please try again later.',
        open: true,
      });
    }
  };

  // Utility to extract filename from fileId (after last slash)
  const extractFilename = (fileId: string) => fileId.substring(fileId.lastIndexOf('/') + 1);

  const handleFileClick = (fileId: string) => {
    const filename = extractFilename(fileId);
    handleFileDownload(filename, fileId);
  };

  const renderTableRow = (row: BulkUploadListItem) => (
    <TableRow key={row.uploadedFileId}>
      <TableCell sx={{ color: Theme.custom.colors.lightTextPrimary }}>
        {format(new Date(row.uploadedAt), 'MM/dd/yyyy')}
      </TableCell>
      <TableCell
        sx={{
          color: Theme.custom.colors.lightPrimaryMain,
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => handleFileClick(row.uploadedFileId)}
      >
        {extractFilename(row.uploadedFileId)}
      </TableCell>
      <TableCell
        sx={{
          color: Theme.custom.colors.lightPrimaryMain,
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => handleFileClick(row.processedFileId)}
      >
        {extractFilename(row.processedFileId)}
      </TableCell>
      <TableCell sx={{ color: Theme.custom.colors.lightTextPrimary }}>
        {row.status
          ? row.status
            .toLowerCase()
            .replace(/\b\w/g, (c: string) => c.toUpperCase())
          : ''}
      </TableCell>
      <TableCell sx={{ color: Theme.custom.colors.lightTextPrimary }}>
        {row.uploadedBy}
      </TableCell>
      <TableCell sx={{ color: Theme.custom.colors.lightTextPrimary }}>
        {row.uploadedFor}
      </TableCell>
    </TableRow>
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {uploadToastProps && (
        <Toast
          {...uploadToastProps}
          onClose={() => setUploadToastProps(null)}
        />
      )}
      <Drawer anchor='right' open={isDrawerOpen} onClose={handleClose} PaperProps={{ sx: { width: '100%', maxWidth: '80%', p: 3, overflowX: 'scroll' } }}>
        <Box display='flex' flexDirection='column' height='100%'>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={0.5}>
            <Typography variant='h6'>Bulk upload history</Typography>
            <IconButton size='small' onClick={handleClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ p: 4 }}>
            <TableContainer
              sx={{
                maxHeight: '75vh',
                overflowY: 'auto',
                backgroundColor: Theme.custom.colors.white,
              }}
            >
              <Table stickyHeader>
                <TableHead
                  sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: Theme.custom.colors.white,
                    zIndex: 1000,
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: Theme.custom.colors.lightTextPrimary,
                      }}
                    >
                      Date uploaded
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: Theme.custom.colors.lightTextPrimary,
                      }}
                    >
                      Uploaded file
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: Theme.custom.colors.lightTextPrimary,
                      }}
                    >
                      Processed file
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: Theme.custom.colors.lightTextPrimary,
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: Theme.custom.colors.lightTextPrimary,
                      }}
                    >
                      Uploaded by
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: Theme.custom.colors.lightTextPrimary,
                      }}
                    >
                      Provider
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.fileUploads && data.fileUploads.length > 0 ? (
                    data.fileUploads.map(renderTableRow)
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ color: Theme.custom.colors.lightTextSecondary, py: 6 }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
